/*---------------------------------------------------------------
 ?? Project: Ring2All Platform
 ????? Author: Rodrigo Cuadra
 ?? Company: Ring2All LLC
 ?? File: MOHPage.tsx
 ??? Created: 2025-01-XX
 ?? Description:
    Music on Hold (MOH) Sources main page with comprehensive CRUD operations.
    
    Refactored to use modular architecture with hooks, components,
    and utilities for better maintainability.

 ?? Last Updated: 2025-01-XX by Rodrigo Cuadra (Refactored)
----------------------------------------------------------------*/

import { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Music, X } from 'lucide-react';
import DataGridSimple from '../../components/ui/DataGridSimple';
import ConfirmationModal from '../../components/ui/ConfirmationModal';
import { useNotifications } from '../../contexts/NotificationContext';
import { useI18n } from '../../hooks/useI18n';
import { ModulePageContainer } from '../../components/layouts';
import { useMohSourcesList } from './hooks';
import { useMohSourcesColumns } from './components/MohSourcesColumns';
import { MohSourcesActions } from './components/MohSourcesActions';
import { MOHSource, MOHSourceFilters, XmlModalState } from './types';
import { deleteMohSource, duplicateMohSource, getMohSourceXml } from './api';

export default function MOHPage() {
  const navigate = useNavigate();
  const { t } = useI18n();
  const { showSuccess, showError } = useNotifications();

  // State management
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [mohToDelete, setMohToDelete] = useState<MOHSource | null>(null);
  const [filters] = useState<MOHSourceFilters>({});
  const [xmlModal, setXmlModal] = useState<XmlModalState>({
    isOpen: false,
    moh: null,
    xml: '',
  });

  // Get column definitions
  const availableColumns = useMohSourcesColumns();

  // Use data grid hook with columns
  const {
    data,
    loading,
    pagination,
    searchQuery,
    sortBy,
    sortOrder,
    columns,
    handleSearch,
    handleSort,
    handlePageChange,
    handleItemsPerPageChange,
    handleColumnToggle,
    refresh,
  } = useMohSourcesList({ initialColumns: availableColumns, filters });

  // Handle create
  const handleCreate = useCallback(() => {
    navigate('/settings/voice-prompts/music-hold/new');
  }, [navigate]);

  // Handle edit
  const handleEdit = useCallback(
    (moh: MOHSource) => {
      navigate(`/settings/voice-prompts/music-hold/${moh.uuid ?? moh.id}`);
    },
    [navigate],
  );

  // Handle duplicate
  const handleDuplicate = useCallback(
    async (moh: MOHSource) => {
      try {
        await duplicateMohSource(moh);
        showSuccess(t('mohSources.messages.duplicateSuccess') || 'MOH source duplicated successfully');
        await refresh();
      } catch (error) {
        showError(
          (error instanceof Error ? error.message : null) || t('mohSources.messages.duplicateError') || 'Failed to duplicate MOH source',
        );
      }
    },
    [showSuccess, showError, refresh, t],
  );

  // Handle delete
  const handleDelete = useCallback((moh: MOHSource) => {
    setMohToDelete(moh);
    setShowDeleteModal(true);
  }, []);

  // Handle delete confirm
  const handleDeleteConfirm = useCallback(async () => {
    if (!mohToDelete) return;

    try {
      await deleteMohSource(mohToDelete.id);
      showSuccess(t('mohSources.messages.deleteSuccess') || 'MOH source deleted successfully');
      setShowDeleteModal(false);
      setMohToDelete(null);
      await refresh();
    } catch (error) {
      showError((error instanceof Error ? error.message : null) || t('mohSources.messages.deleteError') || 'Failed to delete MOH source');
    }
  }, [mohToDelete, showSuccess, showError, refresh, t]);

  // Handle view XML
  const handleViewXml = useCallback(
    async (moh: MOHSource) => {
      try {
        const xmlData = await getMohSourceXml(moh.id);
        setXmlModal({
          isOpen: true,
          moh: moh,
          xml: xmlData,
        });
      } catch (error) {
        const errorMessage = error instanceof Error ? error.message : 'Failed to fetch XML';
        showError(errorMessage);
      }
    },
    [showError],
  );

  // Render actions column
  const renderActions = useCallback(
    (item: MOHSource) => (
      <MohSourcesActions
        item={item}
        onEdit={handleEdit}
        onDuplicate={handleDuplicate}
        onDelete={handleDelete}
        onViewXml={handleViewXml}
      />
    ),
    [handleEdit, handleDuplicate, handleDelete, handleViewXml],
  );

  return (
    <ModulePageContainer
      currentView="list"
      listView={
        <>
          <DataGridSimple
            data={data}
            titleIcon={<Music className="h-5 w-5 text-primary" />}
            loading={loading}
            columns={columns}
            pagination={pagination}
            searchQuery={searchQuery}
            sortBy={sortBy}
            sortOrder={sortOrder}
            onSearch={handleSearch}
            onSort={handleSort}
            onPageChange={handlePageChange}
            onItemsPerPageChange={handleItemsPerPageChange}
            onColumnToggle={handleColumnToggle}
            renderActions={renderActions}
            searchPlaceholder={t('mohSources.searchPlaceholder') || 'Search MOH sources...'}
            title={t('mohSources.title') || 'Music on Hold'}
            onAdd={handleCreate}
            addButtonText={t('common:add') || 'Add MOH Source'}
            translations={{
              items: t('common:items') || 'Items',
              columns: t('common:columns') || 'Columns',
              noItemsFound: t('common:noItemsFound') || 'No items found',
              showing: t('common:showing') || 'Showing',
              to: t('common:to') || 'to',
              of: t('common:of') || 'of',
              results: t('common:results') || 'results',
              previous: t('common:previous') || 'Previous',
              next: t('common:next') || 'Next',
              page: t('common:page') || 'Page',
              deleteItem: t('common:deleteItem') || 'Delete',
              areYouSureDeleteItem: t('common:areYouSure') || 'Are you sure?',
              delete: t('common:delete') || 'Delete',
              cancel: t('common:cancel') || 'Cancel',
              allSelected: t('common:allSelected') || 'All selected',
              selected: t('common:selected') || 'selected',
              searchOptions: t('common:searchOptions') || 'Search options',
              noOptionsFound: t('common:noOptionsFound') || 'No options found',
              selectAll: t('common:selectAll') || 'Select all',
              clearAll: t('common:clearAll') || 'Clear all',
            }}
          />

          <ConfirmationModal
            isOpen={showDeleteModal}
            onClose={() => {
              setShowDeleteModal(false);
              setMohToDelete(null);
            }}
            onConfirm={handleDeleteConfirm}
            title={t('mohSources.actions.delete') || 'Delete MOH Source'}
            message={
              t('mohSources.messages.deleteConfirm', { name: mohToDelete?.name }) ||
              `Are you sure you want to delete "${mohToDelete?.name}"?`
            }
            confirmText={t('common:delete') || 'Delete'}
            cancelText={t('common:cancel') || 'Cancel'}
            type="danger"
          />

          {/* XML View Modal */}
          {xmlModal.isOpen && (
            <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
              <div className="bg-card border border-border rounded-lg p-6 w-full max-w-4xl max-h-[80vh] overflow-hidden">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold text-foreground">
                    {t('extensions.xmlViewer.title')} - {xmlModal.moh?.name}
                  </h3>
                  <button
                    onClick={() => setXmlModal({ isOpen: false, moh: null, xml: '' })}
                    className="p-1 text-muted-foreground hover:text-foreground transition-colors"
                  >
                    <X className="h-4 w-4" />
                  </button>
                </div>

                <div className="bg-muted/30 rounded-lg p-4 max-h-96 overflow-auto">
                  <pre className="text-sm text-foreground whitespace-pre-wrap font-mono">
                    {xmlModal.xml}
                  </pre>
                </div>

                <div className="flex justify-end gap-2 mt-4">
                  <button
                    onClick={() => {
                      navigator.clipboard.writeText(xmlModal.xml);
                      showSuccess(t('extensions.messages.xmlCopied') || 'XML copied to clipboard');
                    }}
                    className="px-4 py-2 bg-secondary text-secondary-foreground rounded-md hover:bg-secondary/90 transition-colors"
                  >
                    {t('extensions.actions.copyXml') || 'Copy XML'}
                  </button>
                  <button
                    onClick={() => setXmlModal({ isOpen: false, moh: null, xml: '' })}
                    className="px-4 py-2 bg-primary text-primary-foreground rounded-md hover:bg-primary/90 transition-colors"
                  >
                    {t('common.close') || 'Close'}
                  </button>
                </div>
              </div>
            </div>
          )}
        </>
      }
      formView={null}
    />
  );
}
